"use client";
/**
 * Team member tags (plans/team-member-tags-spec.md).
 *
 * Business-authored, customer-facing notes on a team member — e.g. the
 * languages they speak. The text is shown as written, never translated.
 *
 * `MemberTagBadges` renders the labels as small tags with 4px corners. Since
 * Oct 21 (standard 2.4.6) they show only inside booking: under a "Useful
 * info" heading in the member info modal, and under the provider on review
 * and confirmation. The picker's member rows and the `/member/[id]` page
 * show none. A tag with a description is a button; pressing it reveals the
 * description as one line of secondary text under the pills (Oct 19: the
 * earlier bordered notes block read heavier than the member's own bio). A
 * tag without a description is a plain pill.
 */
import React, { useId, useState } from "react";
import type { PublicMemberTag } from "@openings/api-contract";
import {
  memberTagActiveStyle,
  memberTagButtonStyle,
  memberTagItemStyle,
  memberTagListStyle,
  memberTagNoteStyle,
  memberTagStyle,
} from "./bookingStyles";

export type MemberTagView = Pick<PublicMemberTag, "id" | "label"> & {
  description?: string | null;
};

function noteOf(tag: MemberTagView): string | null {
  const text = tag.description?.trim();
  return text ? text : null;
}

export function MemberTagBadges({
  tags,
  label = "About this team member",
}: {
  tags?: readonly MemberTagView[] | null;
  /** Accessible name of the list. */
  label?: string;
}) {
  const [openId, setOpenId] = useState<string | null>(null);
  const noteId = useId();
  if (!tags?.length) return null;
  const openTag = tags.find((tag) => tag.id === openId) ?? null;
  const openNote = openTag ? noteOf(openTag) : null;
  return (
    <div className="openings-member-tags">
      <ul aria-label={label} style={memberTagListStyle}>
        {tags.map((tag) => {
          const note = noteOf(tag);
          const isOpen = note !== null && tag.id === openId;
          return (
            <li
              key={tag.id}
              className="openings-member-tag"
              style={memberTagItemStyle}
            >
              {note ? (
                <button
                  type="button"
                  aria-expanded={isOpen}
                  aria-controls={noteId}
                  style={
                    isOpen
                      ? {
                          ...memberTagStyle,
                          ...memberTagButtonStyle,
                          ...memberTagActiveStyle,
                        }
                      : { ...memberTagStyle, ...memberTagButtonStyle }
                  }
                  onClick={() =>
                    setOpenId((current) => (current === tag.id ? null : tag.id))
                  }
                >
                  {tag.label}
                </button>
              ) : (
                <span style={memberTagStyle}>{tag.label}</span>
              )}
            </li>
          );
        })}
      </ul>
      {openNote ? (
        <p
          id={noteId}
          className="openings-member-tag-note"
          style={memberTagNoteStyle}
        >
          {openNote}
        </p>
      ) : null}
    </div>
  );
}
